import React, { useCallback, useState } from "react";
import { AutoComplete, Form, Input as AntInput, message } from "antd";
import { observer } from "mobx-react";
import Props from "./Props";
import apiManager from "../../../../services/Manager";
import { OptionType } from "../../../../stores/form/Interface";

const MINIMUM_CHARS = 2;
const ABORT_ERROR = "AbortError";

let abort: () => void = () => {};

const abortableFetch = (
  name: string,
  searchTest: string
): Promise<Response> => {
  const controller = new AbortController();
  const { signal } = controller;

  abort = () => {
    controller.abort();
  };

  return apiManager.cmrAutocomplete(name as string, searchTest, { signal });
};

interface AutocompleteProps extends Props {
  textarea?: boolean;
}

const Autocomplete: React.FC<AutocompleteProps> = ({
  name,
  config,
  textarea = false,
  ...rest
}) => {
  const [options, setOptions] = useState<OptionType[]>([]);

  const onSearch = useCallback(
    (searchTest: string) => {
      if (searchTest.length < MINIMUM_CHARS) {
        return;
      }

      if (abort) {
        abort();
      }

      abortableFetch(name as string, searchTest)
        .then(async (response) => {
          setOptions((await response.json()) as OptionType[]);
        })
        .catch(async (err) => {
          if (err.name === ABORT_ERROR) {
            return;
          }

          const response = await err.json();

          if (response.errors && Array.isArray(response.errors.value)) {
            message.error(response.errors.value.join(", "));
          }
        });
    },
    [name]
  );

  return (
    <Form.Item
      name={name}
      label={config.label}
      rules={config.validationRules}
      labelCol={{ span: 24 }}
      {...(config.extra || {})}
      {...rest}
    >
      <AutoComplete
        getPopupContainer={(triggerNode) => triggerNode.parentNode}
        options={options}
        onSearch={onSearch}
        placeholder={config.placeholder}
        className={config.className}
      >
        {textarea && <AntInput.TextArea rows={4} />}
      </AutoComplete>
    </Form.Item>
  );
};

export default observer(Autocomplete);
